import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';
import { Link } from 'react-router-dom';

export default function EnPrivacyPolicyPage() {
  useSEO({
    title: 'Privacy Policy | Kang Yong Biotech Ltd.',
    description: 'Kang Yong Biotech privacy policy explaining how we collect, use, store and protect your personal data across our website and all related business activities.',
    keywords: 'privacy policy, personal data protection, Kang Yong Biotech, data security',
    lang: 'en',
    canonicalPath: '/en/privacy-policy',
  });

  return (
    <EnLayout>
      <div className="min-h-screen bg-white pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link to="/en" className="hover:text-gray-600 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-base" />
            <span className="text-gray-600">Privacy Policy</span>
          </nav>

          {/* Header */}
          <div className="mb-10 pb-8 border-b border-gray-100">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
            <p className="text-gray-400 text-sm">Last updated: January 2025</p>
          </div>

          {/* Content */}
          <div className="space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Overview</h2>
              <p className="text-[0.9375rem]">
                Kang Yong Biotech Ltd. (hereinafter "the Company") values your privacy and is committed
                to protecting your personal information. This Privacy Policy explains how the Company
                collects, uses, stores, and protects your personal data in connection with our website
                and all related business activities.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
              <p className="text-[0.9375rem] mb-3">The Company may collect the following information:</p>
              <ul className="list-disc list-inside space-y-2 text-[0.9375rem] pl-2">
                <li>Name, company name, and job title</li>
                <li>Contact phone number and email address</li>
                <li>Inquiry details and cooperation requirements</li>
                <li>Website browsing data (cookies and similar technical information)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p className="text-[0.9375rem] mb-3">
                Personal data collected by the Company is used solely for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[0.9375rem] pl-2">
                <li>Responding to your inquiries and discussing cooperation opportunities</li>
                <li>Providing OEM/ODM quotes and related information</li>
                <li>Improving our website and service quality</li>
                <li>Complying with applicable legal requirements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Protection</h2>
              <p className="text-[0.9375rem]">
                The Company implements appropriate technical and administrative measures to protect
                your personal data from unauthorized access, use, or disclosure. Your information
                is used solely for internal business purposes and will not be sold, rented, or
                shared with third parties without your consent, except as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Cookies</h2>
              <p className="text-[0.9375rem]">
                This website uses cookies and similar technologies to enhance your browsing experience.
                You may disable cookies through your browser settings, though this may affect certain
                features of the site.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights</h2>
              <p className="text-[0.9375rem] mb-3">
                Under applicable data protection law, you have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-[0.9375rem] pl-2">
                <li>Access or request a copy of your personal data</li>
                <li>Request correction or supplementation of your data</li>
                <li>Request cessation of collection, processing, or use of your data</li>
                <li>Request deletion of your personal data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Contact Us</h2>
              <p className="text-[0.9375rem]">
                If you have any questions about this Privacy Policy or wish to exercise your rights,
                please contact us via:
              </p>
              <div className="mt-4 bg-gray-50 rounded-lg p-5 space-y-2 text-[0.9375rem]">
                <p><strong className="text-gray-900">Kang Yong Biotech Ltd.</strong></p>
                <p className="text-gray-600">6F, No. 79, Sec. 1, Zhongqing Rd., North Dist., Taichung City, Taiwan</p>
                <p>
                  Phone:{' '}
                  <a href="tel:+886422029858" className="text-[#2d5a27] hover:underline cursor-pointer">+886-4-2202-9858</a>
                </p>
                <p>
                  Email:{' '}
                  <a href="mailto:natural1070606@gmail.com" className="text-[#2d5a27] hover:underline cursor-pointer">natural1070606@gmail.com</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Policy Updates</h2>
              <p className="text-[0.9375rem]">
                The Company reserves the right to update this Privacy Policy at any time.
                Significant changes will be announced on our website. We recommend checking
                this page periodically to stay informed of any updates.
              </p>
            </section>
          </div>

          {/* Bottom nav */}
          <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <Link
              to="/en"
              className="inline-flex items-center gap-2 text-[#2d5a27] hover:text-[#1a3d18] text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
            <Link
              to="/en/terms-of-use"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#2d5a27] text-sm transition-colors cursor-pointer"
            >
              <i className="ri-file-text-line" />
              View Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </EnLayout>
  );
}
